import { useEffect, useRef, useState } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";

const ease = [0.22, 1, 0.36, 1] as const;

export interface Metric {
  value: number;
  suffix?: string;
  prefix?: string;
  label: string;
  decimals?: number;
}

interface Props {
  metrics: Metric[];
}

const useCountUp = (target: number, decimals: number, start: boolean, duration = 1800) => {
  const [val, setVal] = useState(0);
  const reduce = useReducedMotion();
  useEffect(() => {
    if (!start) return;
    if (reduce) {
      setVal(target);
      return;
    }
    const t0 = performance.now();
    let raf = 0;
    const tick = (t: number) => {
      const p = Math.min(1, (t - t0) / duration);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(target * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [start, target, duration, reduce]);
  return decimals > 0 ? val.toFixed(decimals) : Math.round(val).toString();
};

const MetricCard = ({ m, i, inView }: { m: Metric; i: number; inView: boolean }) => {
  const display = useCountUp(m.value, m.decimals ?? 0, inView);
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, ease, delay: 0.1 + i * 0.1 }}
      className="relative px-6 py-10 border-l border-border first:border-l-0"
    >
      <p className="display text-[44px] md:text-[64px] leading-none text-foreground tabular-nums">
        {m.prefix}
        {display}
        {m.suffix && <span className="text-primary/90">{m.suffix}</span>}
      </p>
      <p className="mt-4 eyebrow">{m.label}</p>
    </motion.div>
  );
};

const MetricCounters = ({ metrics }: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });

  return (
    <div
      ref={ref}
      className="grid grid-cols-2 md:grid-cols-4 border border-border rounded-lg bg-card overflow-hidden"
    >
      {metrics.map((m, i) => (
        <MetricCard key={m.label} m={m} i={i} inView={inView} />
      ))}
    </div>
  );
};

export default MetricCounters;
